import { NgClass } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
    AbstractControl,
    FormArray,
    FormGroup,
    ReactiveFormsModule,
    UntypedFormBuilder,
    ValidatorFn,
    Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SubscriptionManager } from "@ramudden/core/utils";
import { LocationApi } from "@ramudden/data-access/resource/location.api";
import { SignCategoryApi } from "@ramudden/data-access/resource/signcategory.api";
import { TaskRuleApi } from "@ramudden/data-access/resource/task-rule.api";
import { TaskApi } from "@ramudden/data-access/resource/task.api";
import { WorkerApi } from "@ramudden/data-access/resource/worker.api";
import { SigncoFormGroup } from "@ramudden/models/form";
import { IAddress, ICoordinate, ILocation, LocationUpdater } from "@ramudden/models/location";
import { SearchParameters, SortDescriptor, SortDirection } from "@ramudden/models/search";
import { ISignCategory } from "@ramudden/models/signcategory";
import {
    DimensionOption,
    ITask,
    ITaskStatusHistory,
    IStopLineBandStatusHistory,
    ITransitionBandStatusHistory,
    MarkingPropertyTypeOption,
    PrimitiveOption,
    SignColorOption,
    SignMaterial,
    SubstrateType,
    TaskCreator,
    TaskPropertyConfiguration,
    TaskStatus,
    TaskType,
    TaskUpdater,
    Unit,
    WorkerTaskCreator,
} from "@ramudden/models/task";
import { ISignMaterialInfo, ITaskRule } from "@ramudden/models/task-rule";
import { IWorker } from "@ramudden/models/worker";
import { FormValidationService } from "@ramudden/services";
import { PhotoInputComponent, PhotoInputFileLoadEnd, SvgComponent, ToastService } from "@ramudden/ui";
import moment from "moment";
import { firstValueFrom, map } from "rxjs";
import { DropdownComponent } from "../../../../components/dropdown/dropdown.component";
import { DropdownOption } from "../../../../components/dropdown/dropdown.model";
import { FormValidationComponent } from "../../../../components/form-validation/form-validation.component";
import { LoaderIconComponent } from "../../../../components/loader-icon/loader-icon.component";
import { DefaultComponent } from "../../../../layout/content/default/default.component";
import { SafeHtmlPipe } from "../../../../pipes/safe-html.pipe";
import { TranslateModelPipe } from "../../../../pipes/translate-model.pipe";
import { AssignmentService } from "../../../../services/assignment.service";
import { AppService } from "../../../../services/app.service";
import { DomainDataService } from "../../../../services/generic/domain-data.service";
import { GlobalEventsService } from "../../../../services/generic/global-events.service";
import { LoaderService } from "../../../../services/loader.service";
import { LocationService } from "../../../../services/location.service";
import { TaskService } from "../../../../services/task.service";
import { TitleService } from "../../../../services/title.service";
import { MapsDialogComponent } from "../../assignment-parking-bans/edit-parking-ban/dialogs/maps/maps-dialog.component";

export function integerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null;
        }
        return Number.isInteger(control.value) ? null : { invalidInteger: { value: control.value } };
    };
}

@Component({
    standalone: true,
    selector: "app-task-details",
    templateUrl: "./task-details.component.html",
    styleUrls: ["./task-details.component.scss"],
    imports: [
    DefaultComponent,
    DropdownComponent,
    FormValidationComponent,
    LoaderIconComponent,
    MapsDialogComponent,
    NgClass,
    PhotoInputComponent,
    ReactiveFormsModule,
    SafeHtmlPipe,
    SvgComponent,
    TranslateModelPipe,
    TranslateModule
],
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
    task: ITask;

    submitting: boolean;
    taskForm: SigncoFormGroup;
    taskStatusForm: SigncoFormGroup;

    taskStatuses: DropdownOption<string>[];
    filteredTaskStatuses: DropdownOption<string>[];
    taskTypes: DropdownOption<string>[];
    signMaterials: DropdownOption<string>[];
    removeTypes: DropdownOption<string>[];
    signCategoryValues: DropdownOption<string>[];
    signCategoryLevel2Values: DropdownOption<string>[];
    signCategoryLevel3Values: DropdownOption<string>[];
    signCategoryLevel4Values: DropdownOption<string>[];
    signCategoryLevel5Values: DropdownOption<string>[];
    allSubstrateTypes: DropdownOption<string>[];
    workers: DropdownOption<number>[];

    propertyConfiguration = new TaskPropertyConfiguration();

    selectMaterial = false;
    selectColor: boolean;
    selectIsMachineWork: boolean;
    selectCanHavePrimer: boolean;
    selectShowSubstrate: boolean;
    selectShowCategories: boolean;
    selectCanBeRefreshed: boolean;
    selectLevel2Category: boolean;
    selectLevel3Category: boolean;
    selectLevel4Category: boolean;
    selectLevel5Category: boolean;
    generatedName: string;

    displayCustomLength: boolean;
    displayCustomWidth: boolean;
    displayCustomColoredWidth: boolean;
    displayCustomColoredLength: boolean;
    displayCustomDimension: boolean;
    displayCustomDiameter: boolean;
    displayCustomLDimension: boolean;
    displayCustomTDimension: boolean;
    displayCustomHeight: boolean;
    displayCustomText: boolean;
    displayCustomSpeed: boolean;
    displayCustomSignColor: boolean;
    displayCustomLine15WidthWhite = false;
    displayCustomLine15WidthGreen = false;
    displayCustomLine30WidthGreen = false;
    displayCustomTransitionWidth: boolean[] = [];
    displayCustomStopLineWidth: boolean[] = [];

    hideWidthList: boolean;
    hideLengthList: boolean;
    hideHeightList: boolean;
    hideColoredWidthList: boolean;
    hideColoredLengthList: boolean;
    hideDiameterList: boolean;
    hideSpeedList: boolean;
    hideTextList: boolean;
    hideDimensionList: boolean;
    hideLElementDimensionList: boolean;
    hideTElementDimensionList: boolean;
    hideWidth15WhiteList = false;
    hideWidth15GreenList = false;
    hideWidth30GreenList = false;

    widthOptions: DropdownOption<string>[];
    lengthOptions: DropdownOption<string>[];
    coloredWidthOptions: DropdownOption<string>[];
    coloredLengthOptions: DropdownOption<string>[];
    typeOptions: DropdownOption<string>[];
    diameterOptions: DropdownOption<string>[];
    heightOptions: DropdownOption<string>[];
    textOptions: DropdownOption<string>[];
    speedOptions: DropdownOption<string>[];
    dimensionOptions: DropdownOption<DimensionOption>[];
    lElementDimensionOptions: DropdownOption<DimensionOption>[];
    tElementDimensionOptions: DropdownOption<DimensionOption>[];
    signColorOptions: DropdownOption<string>[];
    width15WhiteOptions: DropdownOption<string>[];
    width15GreenOptions: DropdownOption<string>[];
    width30GreenOptions: DropdownOption<string>[];
    transitionWidthOptions: DropdownOption<number>[];
    stopLineWidthOptions: DropdownOption<number>[];

    private allSignMaterials: DropdownOption<string>[];
    private allRemoveTypes: DropdownOption<string>[];
    private allSignCategories: ISignCategory[];
    private allSignCategoryValues: DropdownOption<string>[];
    private allMarkingPropertyTypeValues: DropdownOption<string>[];
    private allSignColorOptions: DropdownOption<string>[];
    private allUnits: DropdownOption<string>[];

    protected currentSignCategory: ISignCategory;
    private currentTaskRule: ITaskRule;
    private taskRules: ITaskRule[];

    private taskRuleIsSet: boolean;
    private readonly subscriptionManager = new SubscriptionManager();

    currentDate: Date;
    signColors = [
        { label: "taskPage.white", value: "#f6f6f6" },
        { label: "taskPage.black", value: "#1e1e1e" },
        { label: "taskPage.yellow", value: "#f0ca00" },
        { label: "taskPage.green", value: "#008754" },
        { label: "taskPage.lightGreen", value: "#48a43f" },
        { label: "taskPage.red", value: "#c1121c" },
        { label: "taskPage.blue", value: "#0e518d" },
        { label: "taskPage.lightBlue", value: "#3481b8" },
        { label: "taskPage.reflectingBlue", value: "#81c0bb" },
        { label: "taskPage.orange", value: "#e15501" },
        { label: "taskPage.ochre", value: "#ba8f4c" },
        { label: "taskPage.purple", value: "#992572" },
        { label: "taskPage.grey", value: "#8f9695" },
    ] as DropdownOption<string>[];

    customTransitionColors = [
        { label: "taskPage.white", value: "#f6f6f6" },
        { label: "taskPage.red", value: "#c1121c" },
        { label: "taskPage.yellow", value: "#f0ca00" },
        { label: "taskPage.orange", value: "#e15501" },
        { label: "taskPage.green", value: "#008754" },
        { label: "taskPage.blue", value: "#0e518d" },
        { label: "taskPage.purple", value: "#992572" },
    ] as DropdownOption<string>[];

    protected readonly appService = inject(AppService);
    private readonly assignmentService = inject(AssignmentService);
    private readonly domainDataService = inject(DomainDataService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly loaderService = inject(LoaderService);
    private readonly locationApi = inject(LocationApi);
    protected readonly locationService = inject(LocationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly signCategoryApi = inject(SignCategoryApi);
    private readonly taskApi = inject(TaskApi);
    private readonly taskRulesApi = inject(TaskRuleApi);
    private readonly taskService = inject(TaskService);
    private readonly titleService = inject(TitleService);
    private readonly toastService = inject(ToastService);
    private readonly workerApi = inject(WorkerApi);
    readonly formValidationService = inject(FormValidationService);
    readonly globalEventsService = inject(GlobalEventsService);
    readonly translateService = inject(TranslateService);

    title: string;

    async ngOnInit() {
        this.subscriptionManager.add("routeChanges", this.subscribeToRouteChanges());
        const plannedAssignmentId = this.route.snapshot.params["plannedAssignmentId"];
        this.titleService.goBack.set(["assignment", plannedAssignmentId, "tasks"]);
        this.titleService.title = this.translateService.instant("taskPage.title");

        let task: ITask;
        if (this.isCreatingNewTask()) {
            task = {} as ITask;
            task.location = {} as ILocation;
            task.assignment = this.assignmentService.selectedPlannedAssignment.assignment;
            task.currentStatus = {} as ITaskStatusHistory;
        } else {
            task = await this.taskService.fetchTask(this.route.snapshot.params["taskId"]);
            this.generatedName = task.name;
        }
        this.task = task;
        this.setTitle();

        this.currentDate = new Date();
        await this.initDropdowns();
        this.filterTaskStatuses();

        // In coordinator view, restrict to Control task type only
        if (this.appService.isCoordinatorControlTask && this.isCreatingNewTask()) {
            this.taskTypes = this.taskTypes.filter(t => t.value === TaskType.Control);
        }

        this.initialize().then(async () => {
            if (this.isCreatingNewTask()) {
                const coordinates = await this.locationService.getCurrentCoordinates().catch(() => {
                    this.toastService.showWarning("location.usedDefault");
                    return this.assignmentService.selectedPlannedAssignment.assignment.location.coordinate;
                });
                const address = await this.locationService.getAddressFromCoordinates(coordinates);
                task.location.coordinate = coordinates;
                task.location.address = address.address;
                this.taskForm.get("location").patchValue(task.location);
            }
        });
    }

    subscribeToRouteChanges() {
        return this.route.params.subscribe(async (params) => {
            if (this.task && !this.isCreatingNewTask()) {
                this.task = await this.taskService.fetchTask(params["taskId"]);
                this.generatedName = this.task.name;
                this.initialize();
                this.setTitle();
            }
        });
    }

    setTitle() {
        if (!this.isCreatingNewTask()) {
            if (this.task?.assignment?.parentAssignment) {
                this.title = `${this.task.assignment.parentAssignment.name} - ${this.task.assignment.name} - ${this.task.name}`;
            } else if (this.task?.assignment) {
                this.title = `${this.task.assignment.name} - ${this.task.name}`;
            } else {
                this.title = this.task.name;
            }
        }
    }

    ngOnDestroy() {
        this.appService.isCoordinatorControlTask = false;
        this.subscriptionManager.clear();
    }

    currentTaskTypeId: TaskType;
    // Depending on the selected type,
    // display corresponding materials (or categories if no material for the type)
    handleTaskTypeChange(event: TaskType) {
        this.currentTaskTypeId = event;
        const rule = this.taskRules ? this.taskRules.find((r) => r.taskTypeId === event) : undefined;
        if (!rule) return;

        this.refreshTaskRule(rule);
        this.setCategoryValues(undefined);

        this.resetFormControlValues(
            "signMaterialId",
            "signCategoryId",
            "signCategoryLevel2Id",
            "signCategoryLevel3Id",
            "signCategoryLevel4Id",
            "signCategoryLevel5Id",
            "isMachineWork",
            "isNew",
            "customSignColor",
            "signColorId",
        );

        if (!this.selectShowCategories) {
            this.taskStatusForm.controls["signCategoryId"].patchValue(this.signCategoryValues[0].value);
            this.handleCategoryChange(this.signCategoryValues[0].value);
        }
        this.refreshRequiredValidator("signMaterialId", () => this.selectMaterial);

        const atLeastOneValidator = (formGroup: FormGroup) => {
            const hoursCount = formGroup.get("hoursCount")?.value;
            const minutesCount = formGroup.get("minutesCount")?.value;
            return hoursCount || minutesCount ? null : { requireAtLeastOne: true };
        };
        if (event === TaskType.Drying || event === TaskType.Brushing || event === TaskType.Waiting) {
            this.taskStatusForm.addValidators(atLeastOneValidator);
            this.taskStatusForm.get("hoursCount").addValidators([Validators.min(0)]);
            this.taskStatusForm.get("minutesCount").addValidators([Validators.min(0)]);
            this.taskStatusForm.updateValueAndValidity();
        } else {
            this.taskStatusForm.clearValidators();
            this.taskStatusForm.get("hoursCount").clearValidators();
            this.taskStatusForm.get("minutesCount").clearValidators();
            this.taskStatusForm.updateValueAndValidity();
        }
    }

    private getRemoveTypeInfo(removeType: string) {
        const rule = this.taskRules ? this.taskRules.find((r) => r.taskTypeId === this.currentTaskTypeId) : undefined;
        if (!rule) return null;

        return rule.removeTypeInfo.find((r) => r.removeTypeId === removeType);
    }

    handleRemoveTypeChange(event: string) {
        const removeTypeInfo = this.getRemoveTypeInfo(event);
        this.resetCategoryLevels();
        if (!removeTypeInfo) return;

        this.resetFormControlValues(
            "signMaterialId",
            "signCategoryId",
            "signCategoryLevel2Id",
            "signCategoryLevel3Id",
            "signCategoryLevel4Id",
            "signCategoryLevel5Id",
            "isMachineWork",
            "substrateTypeId",
            "substrateTypeOverride",
        );

        this.signMaterials = removeTypeInfo.signMaterialInfo.map((smi) =>
            this.allSignMaterials.find((m) => m.value === smi.signMaterialId),
        );
    }

    // Depending on the selected material,
    // display corresponding categories
    handleMaterialChange(event: SignMaterial) {
        const currentMaterial = this.getMaterialInfo(event);
        this.setSelectIsMachineWork(currentMaterial);
        this.setSelectCanHavePrimer(currentMaterial);
        this.setSelectColor(currentMaterial);

        const previousCategoryId = this.taskStatusForm.controls["signCategoryId"].value;
        this.setCategoryValues(currentMaterial);

        // Keep the selected category if it is still valid for the new material,
        // so the user does not lose their category (and its fields) on every material change.
        if (previousCategoryId && this.isSignCategoryAvailable(previousCategoryId)) {
            this.resetFormControlValues("isMachineWork", "customSignColor", "signColorId");
            this.handleCategoryChange(previousCategoryId);
        } else {
            this.resetCategoryLevels();
            this.resetFormControlValues("signCategoryId", "isMachineWork", "customSignColor", "signColorId");
        }
    }

    // Depending on the selected category,
    // display corresponding sublevel categories
    handleCategoryChange(event: string, initialCall = false) {
        const currentCategory = this.allSignCategories.find((m) => m.signCategoryId === event);
        this.setDefaultColor();
        this.setLevel2Category(currentCategory, initialCall);
        this.setLevel3Category(undefined, initialCall);
        this.setLevel4Category(undefined, initialCall);
        this.setLevel5Category(undefined, initialCall);

        this.handleLengthChange(this.taskStatusForm.get("length").value);
        this.handleWidthChange(this.taskStatusForm.get("width").value);
        this.handleHeightChange(this.taskStatusForm.get("height").value);
    }

    handleCategoryLevel2Change(event: string, initialCall = false) {
        const currentCategory = this.allSignCategories.find((m) => m.signCategoryId === event);
        this.setLevel3Category(currentCategory, initialCall);
        this.setLevel4Category(undefined, initialCall);
        this.setLevel5Category(undefined, initialCall);

        this.handleLengthChange(this.taskStatusForm.get("length").value);
        this.handleWidthChange(this.taskStatusForm.get("width").value);
        this.handleHeightChange(this.taskStatusForm.get("height").value);
    }

    handleCategoryLevel3Change(event: string, initialCall = false) {
        const currentCategory = this.allSignCategories.find((m) => m.signCategoryId === event);

        this.setLevel4Category(currentCategory, initialCall);
        this.setLevel5Category(undefined, initialCall);

        this.handleLengthChange(this.taskStatusForm.get("length").value);
        this.handleWidthChange(this.taskStatusForm.get("width").value);
        this.handleHeightChange(this.taskStatusForm.get("height").value);
    }

    handleCategoryLevel4Change(event: string, initialCall = false) {
        const currentCategory = this.allSignCategories.find((m) => m.signCategoryId === event);

        this.setLevel5Category(currentCategory, initialCall);

        this.handleLengthChange(this.taskStatusForm.get("length").value);
        this.handleWidthChange(this.taskStatusForm.get("width").value);
        this.handleHeightChange(this.taskStatusForm.get("height").value);
    }

    handleCategoryLevel5Change(event: string, initialCall = false) {
        const currentCategory = this.allSignCategories.find((m) => m.signCategoryId === event);

        // if category is present immediately configure because there is no level 5
        if (currentCategory) {
            this.currentSignCategory = currentCategory;
        }
        if (currentCategory && !initialCall) {
            this.applyNewPropertyConfiguration(currentCategory, false);
        }

        this.handleLengthChange(this.taskStatusForm.get("length").value);
        this.handleWidthChange(this.taskStatusForm.get("width").value);
        this.handleHeightChange(this.taskStatusForm.get("height").value);
    }

    // Allowed sign categories are different for machine work and handwork
    handleIsMachineWorkChange() {
        const materialId = this.taskStatusForm.controls["signMaterialId"].value;
        const currentMaterial = this.currentTaskRule?.signMaterialInfo?.find((m) => m.signMaterialId === materialId);
        const previousCategoryId = this.taskStatusForm.controls["signCategoryId"].value;
        this.setCategoryValues(currentMaterial);

        // Keep the selected category if it is still valid for the new machine-work setting.
        if (previousCategoryId && this.isSignCategoryAvailable(previousCategoryId)) {
            this.handleCategoryChange(previousCategoryId);
        } else {
            this.resetFormControlValues("signCategoryId");
            this.resetCategoryLevels();
        }
    }

    /** Whether the given category is part of the currently available category options. */
    private isSignCategoryAvailable(signCategoryId: string): boolean {
        return this.signCategoryValues?.some((c) => c.value === signCategoryId) ?? false;
    }

    handleSubstrateTypeChange(event: string) {
        if (event == SubstrateType.Others) {
            this.taskStatusForm.controls["substrateTypeOverride"].enable();
        } else {
            this.taskStatusForm.controls["substrateTypeOverride"].disable();
        }
        this.refreshRequiredValidator("substrateTypeId", () => event != "");
        this.refreshRequiredValidator("substrateTypeOverride", () => event == "");
    }

    handleWidthChange(event: string) {
        this.displayCustomWidth = event == null;
    }

    handleLengthChange(event: string) {
        this.displayCustomLength = event == null;
    }

    handleLine15WhiteWidthChanged(event: string) {
        this.displayCustomLine15WidthWhite = event == null;
    }

    handleLine15GreenWidthChanged(event: string) {
        this.displayCustomLine15WidthGreen = event == null;
    }

    handleLine30GreenWidthChanged(event: string) {
        this.displayCustomLine30WidthGreen = event == null;
    }

    handleTransitionWidthChange(event: string, index: number) {
        this.displayCustomTransitionWidth[index] = event == null;
    }

    handleStopLineWidthChange(event: string, index: number) {
        this.displayCustomStopLineWidth[index] = event == null;
    }

    handleHeightChange(event: string) {
        this.displayCustomHeight = event == null;
    }

    handleColoredWidthChange(event: string) {
        this.displayCustomColoredWidth = event == null;
    }

    handleColoredLengthChange(event: string) {
        this.displayCustomColoredLength = event == null;
    }

    handleDiameterChange(event: string) {
        this.displayCustomDiameter = event == null;
    }

    handleSpeedChange(event: string) {
        this.displayCustomSpeed = event == null;
    }

    handleTextChange(event: string) {
        this.displayCustomText = event == null;
    }

    handleDimensionChange(event: string) {
        this.displayCustomDimension = event == null;
    }

    handleLElementDimensionChange(event: DropdownOption<DimensionOption>) {
        this.displayCustomLDimension = event == null;
    }

    handleTElementDimensionChange(event: string) {
        this.displayCustomTDimension = event == null;
    }

    get customTransitionsForm(): FormArray<SigncoFormGroup> {
        return this.taskStatusForm.get("customTransitions") as unknown as FormArray<SigncoFormGroup>;
    }

    // Add method to add a new custom transition
    addCustomTransition(width: number = null, length: number = null, count: number = null, color: string = null): void {
        const index = this.customTransitionsForm.length;
        this.customTransitionsForm.push(this.createCustomTransitionFormGroup(width, length, count, color));

        // Initialize display flag - if no options or width not in options, show custom input
        if (!this.transitionWidthOptions) {
            this.displayCustomTransitionWidth[index] = true;
        } else if (width != null) {
            const widthInOptions = this.transitionWidthOptions.find((opt) => opt.value === width);
            this.displayCustomTransitionWidth[index] = !widthInOptions;
            if (widthInOptions) {
                this.customTransitionsForm.at(index).get("transitionWidth").patchValue(width);
            } else {
                this.customTransitionsForm.at(index).get("transitionWidth").patchValue(null);
            }
        } else {
            // Default to showing dropdown with null value (which will show custom input due to handleTransitionWidthChange)
            this.displayCustomTransitionWidth[index] = false;
        }
    }

    // Add method to remove a custom transition
    removeCustomTransition(index: number): void {
        this.customTransitionsForm.removeAt(index);
        this.displayCustomTransitionWidth.splice(index, 1);
    }

    private createCustomTransitionFormGroup(
        width: number,
        length: number,
        count: number,
        color: string,
    ): SigncoFormGroup {
        return this.formBuilder.group({
            transitionWidth: [width, null],
            customTransitionWidth: [width, Validators.required],
            customTransitionLength: [length, Validators.required],
            customTransitionCount: [count, Validators.required],
            customTransitionColor: [color, null],
        }) as SigncoFormGroup;
    }

    get customStopLinesForm(): FormArray<SigncoFormGroup> {
        return this.taskStatusForm.get("customStopLines") as unknown as FormArray<SigncoFormGroup>;
    }

    addCustomStopLine(width: number = null, length: number = null, count: number = null): void {
        const index = this.customStopLinesForm.length;
        this.customStopLinesForm.push(this.createCustomStopLineFormGroup(width, length, count));

        if (!this.stopLineWidthOptions) {
            this.displayCustomStopLineWidth[index] = true;
        } else if (width != null) {
            const widthInOptions = this.stopLineWidthOptions.find((opt) => opt.value === width);
            this.displayCustomStopLineWidth[index] = !widthInOptions;
            if (widthInOptions) {
                this.customStopLinesForm.at(index).get("stopLineWidth").patchValue(width);
            } else {
                this.customStopLinesForm.at(index).get("stopLineWidth").patchValue(null);
            }
        } else {
            this.displayCustomStopLineWidth[index] = false;
        }
    }

    removeCustomStopLine(index: number): void {
        this.customStopLinesForm.removeAt(index);
        this.displayCustomStopLineWidth.splice(index, 1);
    }

    private createCustomStopLineFormGroup(
        width: number,
        length: number,
        count: number,
    ): SigncoFormGroup {
        return this.formBuilder.group({
            stopLineWidth: [width, null],
            customStopLineWidth: [width, Validators.required],
            customStopLineLength: [length, Validators.required],
            customStopLineCount: [count, Validators.required],
        }) as SigncoFormGroup;
    }

    //#region [Reactive form setup]
    private async initialize() {
        if (!this.task) return;

        this.taskStatusForm = this.formBuilder.group({
            substrateTypeOverride: [{ value: "", disabled: true }],
            substrateTypeId: [null],
            taskTypeId: [null, Validators.required],
            removeTypeId: null,
            signMaterialId: null,
            signCategoryId: [null, Validators.required],
            signCategoryLevel2Id: null,
            signCategoryLevel3Id: null,
            signCategoryLevel4Id: null,
            signCategoryLevel5Id: null,
            isMachineWork: null,
            primer: null,
            isNew: null,
            hoursCount: null,
            minutesCount: null,
            note: "",
            customSignColor: "",
            signColorId: null,
            markingsCount: [null, integerValidator()],
            length: null,
            customLength: null,
            width: null,
            customWidth: null,
            coloredLength: null,
            customColoredLength: null,
            coloredWidth: null,
            customColoredWidth: null,
            customDimensionWidth: null,
            customDimensionLength: null,
            customDimensionDescription: null,
            dropdownDimension: null,
            type: "",
            bannerCount: [null, integerValidator()],
            logoCount: [null, integerValidator()],
            diameter: null,
            customDiameter: null,
            dropdownTElementDimension: null,
            customTElementLength: null,
            customTElementWidth: null,
            dropdownLElementDimension: null,
            customLElementLength: null,
            customLElementWidth: null,
            tElementCount: [null, integerValidator()],
            lElementCount: [null, integerValidator()],
            tramElementCount: [null, integerValidator()],
            height: [null],
            customHeight: null,
            text: "",
            customText: "",
            speed: null,
            customSpeed: null,
            placenumber: "",
            block1Amount: [null, integerValidator()],
            block1Width: null,
            block1Length: null,
            block2Amount: [null, integerValidator()],
            block2Width: null,
            block2Length: null,
            lengthA: null,
            lengthB: null,
            lengthC: null,
            lengthD: null,
            whiteSubstrateLength: null,
            whiteSubstrateWidth: null,
            whiteBandCount: [null, integerValidator()],
            redBandCount: [null, integerValidator()],
            yellowBandCount: [null, integerValidator()],
            orangeBandCount: [null, integerValidator()],
            greenBandCount: [null, integerValidator()],
            blueBandCount: [null, integerValidator()],
            purpleBandCount: [null, integerValidator()],
            bendCount: [null, integerValidator()],
            arrowHeadCount: [null, integerValidator()],
            roundedEndCount: [null, integerValidator()],
            shortPieceCount: [null, integerValidator()],
            lengthCross: [null],
            widthCross: [null],
            lengthTriangle: [null],
            widthTriangle: [null],
            line15LengthWhite: [null],
            line15WidthWhite: [null],
            customLine15WidthWhite: [null],
            line15LengthGreen: [null],
            line15WidthGreen: [null],
            customLine15WidthGreen: [null],
            line30LengthGreen: [null],
            line30WidthGreen: [null],
            customLine30WidthGreen: [null],
            mechelenCarSharingTextAmount: [null, integerValidator()],
            octopusZeroBlackCount: [null, integerValidator()],
            octopusThreeBlackCount: [null, integerValidator()],
            octopusSchoolTextYellowCount: [null, integerValidator()],
            octopusSquaresCount: [null, integerValidator()],
            octopusFullLineYellowCount: [null, integerValidator()],
            octopusSchoolTextBlackYellowCount: [null, integerValidator()],
            octopusThirtyYellowBlackCount: [null, integerValidator()],
            customTransitions: this.formBuilder.array([]),
            customStopLines: this.formBuilder.array([]),
        }) as SigncoFormGroup;

        this.taskForm = this.formBuilder.group({
            assignmentId: [null, Validators.required],
            name: ["", Validators.required],
            location: [{} as ILocation, Validators.required],
            currentStatus: this.taskStatusForm,
            attachmentsForm: this.formBuilder.array([]),
            isFinished: [false],
        }) as SigncoFormGroup;

        this.resetTaskRule();
        this.resetTaskProperties();

        this.taskForm.patchValue(this.task);

        if (this.task && this.task?.attachments) {
            this.task.attachments.forEach((attachment) => {
                const group = this.formBuilder.group({
                    attachmentId: attachment.id,
                    attachmentImage: attachment.url,
                    attachmentDescription: attachment.description,
                });

                this.attachments.push(group);
            });
        }

        this.applyTaskRule(this.task);
        this.filterTaskStatuses();
        this.patchAssignment(this.task);

        if (this.isCreatingNewTask()) {
            this.generateName();
        }

        if (this.task?.currentStatus?.taskStatusId === TaskStatus.Finished) {
            this.taskForm.get("isFinished").patchValue(true);
        }

        if (this.task.location) {
            this.taskForm.get("location").patchValue(this.task.location);
        }

        if (this.task.assignment && this.task.assignment.id) {
            this.taskForm.get("assignmentId").disable();
        }

        this.taskForm.markAsPristine();

        setTimeout(() => {
            if (this.editableAssignment) {
                this.taskForm.enable();

                if (this.task.assignment && this.task.assignment.id) {
                    this.taskForm.get("assignmentId")?.disable();
                }
            } else {
                this.taskStatusForm.disable();
                this.taskForm.disable();
            }
        }, 50);
    }

    get editableAssignment() {
        if (this.appService.isCoordinatorControlTask) return true;
        if (this.appService.isReadonlyView) return false;
        return this.assignmentService.selectedPlannedAssignment.isPlannedToday;
    }

    private patchPropertyLists(propertyConfiguration: TaskPropertyConfiguration) {
        // Width
        this.widthOptions = this.setPrimitivePropertyConfiguration(
            "width",
            propertyConfiguration.showWidth,
            propertyConfiguration.widths,
            true,
            propertyConfiguration.widthUnit,
        );
        if (!this.widthOptions && propertyConfiguration.showWidth) {
            this.displayCustomWidth = true;
            this.hideWidthList = true;
        }
        // Width 15 White
        this.width15WhiteOptions = this.setPrimitivePropertyConfiguration(
            "line15WidthWhite",
            propertyConfiguration.showLine15WidthWhite,
            propertyConfiguration.line15WidthWhiteOptions,
            true,
            propertyConfiguration.line15WidthWhiteUnit,
        );

        if (!this.width15WhiteOptions && propertyConfiguration.showLine15WidthWhite) {
            this.displayCustomLine15WidthWhite = true;
            this.hideWidth15WhiteList = true;
        }
        // Width 15 Green
        this.width15GreenOptions = this.setPrimitivePropertyConfiguration(
            "line15WidthGreen",
            propertyConfiguration.showLine15WidthGreen,
            propertyConfiguration.line15WidthGreenOptions,
            true,
            propertyConfiguration.line15WidthGreenUnit,
        );

        if (!this.width15GreenOptions && propertyConfiguration.showLine15WidthGreen) {
            this.displayCustomLine15WidthGreen = true;
            this.hideWidth15GreenList = true;
        }
        // Width 30 Green
        this.width30GreenOptions = this.setPrimitivePropertyConfiguration(
            "line30WidthGreen",
            propertyConfiguration.showLine30WidthGreen,
            propertyConfiguration.line30WidthGreenOptions,
            true,
            propertyConfiguration.line30WidthGreenUnit,
        );

        if (!this.width30GreenOptions && propertyConfiguration.showLine30WidthGreen) {
            this.displayCustomLine30WidthGreen = true;
            this.hideWidth30GreenList = true;
        }
        // Length
        this.lengthOptions = this.setPrimitivePropertyConfiguration(
            "length",
            propertyConfiguration.showLength,
            propertyConfiguration.lengths,
            true,
            propertyConfiguration.lengthUnit,
        );

        if (!this.lengthOptions && propertyConfiguration.showLength) {
            this.displayCustomLength = true;
            this.hideLengthList = true;
        }
        // Height
        this.heightOptions = this.setPrimitivePropertyConfiguration(
            "height",
            propertyConfiguration.showHeight,
            propertyConfiguration.heights,
            true,
            propertyConfiguration.heightUnit,
        );
        if (!this.heightOptions && propertyConfiguration.showHeight) {
            this.displayCustomHeight = true;
            this.hideHeightList = true;
        }
        // ColoredWidth
        this.coloredWidthOptions = this.setPrimitivePropertyConfiguration(
            "coloredWidth",
            propertyConfiguration.showColoredWidth,
            propertyConfiguration.coloredWidths,
            true,
            propertyConfiguration.coloredWidthUnit,
        );
        if (!this.coloredWidthOptions && propertyConfiguration.showColoredWidth) {
            this.displayCustomColoredWidth = true;
            this.hideColoredWidthList = true;
        }
        this.coloredLengthOptions = this.setPrimitivePropertyConfiguration(
            "coloredLength",
            propertyConfiguration.showColoredLength,
            propertyConfiguration.coloredLenghts,
            true,
            propertyConfiguration.coloredLengthUnit,
        );
        if (!this.coloredLengthOptions && propertyConfiguration.showColoredLength) {
            this.displayCustomColoredLength = true;
            this.hideColoredLengthList = true;
        }
        // Diameter
        this.diameterOptions = this.setPrimitivePropertyConfiguration(
            "diameter",
            propertyConfiguration.showDiameter,
            propertyConfiguration.diameters,
            true,
            propertyConfiguration.diameterUnit,
        );
        if (!this.diameterOptions && propertyConfiguration.showDiameter) {
            this.displayCustomDiameter = true;
            this.hideDiameterList = true;
        }
        // Speed
        this.speedOptions = this.setPrimitivePropertyConfiguration(
            "speed",
            propertyConfiguration.showSpeed,
            propertyConfiguration.speeds,
        );
        if (!this.speedOptions && propertyConfiguration.showSpeed) {
            this.displayCustomSpeed = true;
            this.hideSpeedList = true;
        }
        // Text
        this.textOptions = this.setPrimitivePropertyConfiguration(
            "text",
            propertyConfiguration.showText,
            propertyConfiguration.texts,
        );
        if (!this.textOptions && propertyConfiguration.showText) {
            this.displayCustomText = true;
            this.hideTextList = true;
        } else if (this.textOptions && this.taskStatusForm.get("customText").value) {
            this.displayCustomText = true;
        }
        // Type
        this.typeOptions = this.setMarkingPropertyTypeConfiguration(
            "type",
            propertyConfiguration.showType,
            propertyConfiguration.types,
        );
        // Dimension
        this.dimensionOptions = this.setDimensionPropertyConfiguration(
            "dropdownDimension",
            this.task.currentStatus.dimensionWidth,
            this.task.currentStatus.dimensionLength,
            propertyConfiguration.showDimension,
            propertyConfiguration.dimensions,
            propertyConfiguration.dimensionUnit,
        );
        if (!this.dimensionOptions && propertyConfiguration.showDimension) {
            this.displayCustomDimension = true;
            this.hideDimensionList = true;
        }
        // LElement Dimension
        this.lElementDimensionOptions = this.setDimensionPropertyConfiguration(
            "dropdownLElementDimension",
            this.task.currentStatus.lElementWidth,
            this.task.currentStatus.lElementLength,
            propertyConfiguration.showLElementDimension,
            propertyConfiguration.lElementDimensions,
            propertyConfiguration.lElementUnit,
        );
        if (!this.lElementDimensionOptions && propertyConfiguration.showLElementDimension) {
            this.displayCustomLDimension = true;
            this.hideLElementDimensionList = true;
        }
        // TElement Dimension
        this.tElementDimensionOptions = this.setDimensionPropertyConfiguration(
            "dropdownTElementDimension",
            this.task.currentStatus.tElementWidth,
            this.task.currentStatus.tElementLength,
            propertyConfiguration.showTElementDimension,
            propertyConfiguration.tElementDimensions,
            propertyConfiguration.tElementUnit,
        );
        if (!this.tElementDimensionOptions && propertyConfiguration.showTElementDimension) {
            this.displayCustomTDimension = true;
            this.hideTElementDimensionList = true;
        }
        // SignColorType
        this.signColorOptions = this.setSignColorTypeConfiguration(
            "signColorId",
            propertyConfiguration.showSignColorList,
            propertyConfiguration.signColors,
        );
        if (!this.signColorOptions && this.selectColor && !propertyConfiguration.showSignColorList && !propertyConfiguration.hideColor) {
            this.displayCustomSignColor = true;
            this.setDefaultColor();
        }

        // Transition Width Options
        if (propertyConfiguration.showCustomTransition && propertyConfiguration.customTransitionWidthOptions) {
            const unitLabel = this.getUnit(propertyConfiguration.customTransitionWidthUnit);
            const options: DropdownOption<number>[] = [];
            options.push({ label: this.translateService.instant("taskPage.other"), value: null });
            options.push(
                ...propertyConfiguration.customTransitionWidthOptions.map((x) => ({
                    label: x.value.toString() + (unitLabel !== "" ? " " + unitLabel : ""),
                    value: x.value,
                })),
            );
            this.transitionWidthOptions = options;
        }

        // Stop Line Width Options
        if (propertyConfiguration.showCustomStopLine && propertyConfiguration.customStopLineWidthOptions) {
            const unitLabel = this.getUnit(propertyConfiguration.customStopLineWidthUnit);
            const options: DropdownOption<number>[] = [];
            options.push({ label: this.translateService.instant("taskPage.other"), value: null });
            options.push(
                ...propertyConfiguration.customStopLineWidthOptions.map((x) => ({
                    label: x.value.toString() + (unitLabel !== "" ? " " + unitLabel : ""),
                    value: x.value,
                })),
            );
            this.stopLineWidthOptions = options;
        }

        // Block1 width
        if (propertyConfiguration.showBlock1Width) {
            const value =
                this.task.currentStatus.propertyConfiguration?.block1Width ?? propertyConfiguration.block1Width;
            this.taskStatusForm.get("block1Width").patchValue(value);
        }
        // Block1 length
        if (propertyConfiguration.showBlock1Length) {
            const value =
                this.task.currentStatus.propertyConfiguration?.block1Length ?? propertyConfiguration.block1Length;
            this.taskStatusForm.get("block1Length").patchValue(value);
            this.taskStatusForm.get("block1Length").addValidators(Validators.required);
        } else {
            this.taskStatusForm.get("block1Length").clearValidators();
        }

        // Block2 width
        if (propertyConfiguration.showBlock2Width) {
            const value =
                this.task.currentStatus.propertyConfiguration?.block2Width ?? propertyConfiguration.block2Width;
            this.taskStatusForm.get("block2Width").patchValue(value);
        }

        // Block2 length
        if (propertyConfiguration.showBlock2Length) {
            const value =
                this.task.currentStatus.propertyConfiguration?.block2Length ?? propertyConfiguration.block2Length;
            this.taskStatusForm.get("block2Length").patchValue(value);
            this.taskStatusForm.get("block2Length").addValidators(Validators.required);
        } else {
            this.taskStatusForm.get("block2Length").clearValidators();
        }

        if (propertyConfiguration.showCustomTransition && this.task.currentStatus.transitionBandStatusHistory) {
            this.task.currentStatus.transitionBandStatusHistory.forEach((transitionBandStatus) => {
                this.addCustomTransition(
                    transitionBandStatus.width,
                    transitionBandStatus.length,
                    transitionBandStatus.markingsCount,
                    transitionBandStatus.color,
                );
            });
        }

        if (propertyConfiguration.showCustomStopLine && this.task.currentStatus.stopLineBandStatusHistory) {
            this.task.currentStatus.stopLineBandStatusHistory.forEach((stopLineBandStatus) => {
                this.addCustomStopLine(
                    stopLineBandStatus.width,
                    stopLineBandStatus.length,
                    stopLineBandStatus.markingsCount,
                );
            });
        }
    }

    private setMarkingPropertyTypeConfiguration(
        name: string,
        showProperty: boolean,
        defaultValues: MarkingPropertyTypeOption[],
    ): DropdownOption<string>[] {
        if (showProperty && defaultValues && defaultValues.length > 0) {
            const options = this.allMarkingPropertyTypeValues.filter(
                (m) => defaultValues.findIndex((c) => c.value === m.value) > -1,
            );
            if (this.taskStatusForm.get(name).value == null) {
                // set standard value
                const standardOption = defaultValues.find((x) => x.standard === true);
                this.taskStatusForm.get(name).patchValue(standardOption != null ? standardOption.value : null);
            }
            return options;
        }
        return undefined;
    }

    private setSignColorTypeConfiguration(
        name: string,
        showProperty: boolean,
        defaultValues: SignColorOption[],
    ): DropdownOption<string>[] {
        if (showProperty && defaultValues && defaultValues.length > 0) {
            const options = this.allSignColorOptions.filter(
                (m) => defaultValues.findIndex((c) => c.value === m.value) > -1,
            );
            if (this.taskStatusForm.get(name).value == null) {
                // set standard value
                const standardOption = defaultValues.find((x) => x.standard === true);
                this.taskStatusForm.get(name).patchValue(standardOption != null ? standardOption.value : null);
            }
            return options;
        }
        return undefined;
    }

    private setDimensionPropertyConfiguration(
        nameDimension: string,
        width: number,
        length: number,
        showProperty: boolean,
        defaultValues: DimensionOption[],
        unit: Unit = null,
    ): DropdownOption<DimensionOption>[] {
        if (showProperty && defaultValues && defaultValues.length > 0) {
            const unitLabel = this.getUnit(unit);

            const options: DropdownOption<DimensionOption>[] = [];
            options.push({ label: this.translateService.instant("taskPage.other"), value: null });
            options.push(
                ...defaultValues.map((x) => {
                    return {
                        label:
                            x.length +
                            " x " +
                            x.width +
                            (unitLabel !== "" ? " " + unitLabel : "") +
                            (x.description ? " - " + x.description : ""),
                        value: x,
                    } as DropdownOption<DimensionOption>;
                }),
            );
            if (width != null && length != null) {
                const newDimension = {
                    length: length,
                    width: width,
                } as DimensionOption;
                if (!options.find((x) => x.value === newDimension)) {
                    const newOption = {
                        label:
                            newDimension.length +
                            " x " +
                            newDimension.width +
                            (unitLabel !== "" ? " " + unitLabel : "") +
                            (newDimension.description ? " - " + newDimension.description : ""),
                        value: newDimension,
                    } as DropdownOption<DimensionOption>;
                    options.push(newOption);
                    options.sortBy((x) => (x.value ? x.value.length : 0));
                    this.taskStatusForm.get(nameDimension).patchValue(newDimension);
                }
            } else {
                // set standard value
                const standardOption = defaultValues.find((x) => x.standard === true);
                const patchValue = standardOption != null ? standardOption : null;
                this.taskStatusForm.get(nameDimension).patchValue(patchValue);
                if (patchValue === null) this.handleDimensionChange(null);
            }
            return options;
        }
        return undefined;
    }

    private setPrimitivePropertyConfiguration(
        name: string,
        showProperty: boolean,
        defaultValues: PrimitiveOption[],
        hasEmptyOption = true,
        unit: Unit = null,
    ): DropdownOption<string>[] {
        if (showProperty && defaultValues && defaultValues.length > 0) {
            const unitLabel = this.getUnit(unit);

            const options: DropdownOption<string>[] = [];
            if (hasEmptyOption) options.push({ label: this.translateService.instant("taskPage.other"), value: null });
            options.push(
                ...defaultValues.map((x) => ({
                    label: x.value.toString() + (unitLabel !== "" ? " " + unitLabel : ""),
                    value: x.value,
                })),
            );
            if (this.taskStatusForm.get(name).value != null) {
                if (!options.find((x) => x.value === this.taskStatusForm.get(name).value)) {
                    const newOption = {
                        label:
                            this.taskStatusForm.get(name).value.toString() + (unitLabel !== "" ? " " + unitLabel : ""),
                        value: this.taskStatusForm.get(name).value,
                    } as DropdownOption<string>;
                    options.push(newOption);
                    const firstNonNullValue = options.find((x) => x.value != null);
                    if (firstNonNullValue && typeof firstNonNullValue.value === "number") {
                        options.sort((x, y) => Number(x.value) - Number(y.value));
                    } else if (firstNonNullValue && typeof firstNonNullValue.value === "string") {
                        options.orderBy((x) => x.value);
                    }
                }
            } else {
                // set standard value
                const standardOption = defaultValues.find((x) => x.standard === true);
                this.taskStatusForm.get(name).patchValue(standardOption != null ? standardOption.value : null);
            }
            return options;
        }
        return undefined;
    }

    private patchAssignment(task: ITask) {
        const assignmentId = task.assignment ? task.assignment.id : undefined;
        this.taskForm.controls["assignmentId"].patchValue(assignmentId);
    }

    private patchTaskRuleFormValues(task: ITask) {
        this.taskStatusForm.controls["taskTypeId"].patchValue(task.currentStatus.taskTypeId);
        this.taskStatusForm.controls["signMaterialId"].patchValue(task.currentStatus.signMaterialId);
        this.taskStatusForm.controls["signCategoryId"].patchValue(task.currentStatus.signCategoryId);
        this.taskStatusForm.controls["signCategoryLevel2Id"].patchValue(task.currentStatus.signCategoryLevel2Id);
        this.taskStatusForm.controls["signCategoryLevel3Id"].patchValue(task.currentStatus.signCategoryLevel3Id);
        this.taskStatusForm.controls["signCategoryLevel4Id"].patchValue(task.currentStatus.signCategoryLevel4Id);
        this.taskStatusForm.controls["signCategoryLevel5Id"].patchValue(task.currentStatus.signCategoryLevel5Id);
        this.taskStatusForm.controls["substrateTypeId"].patchValue(
            task.currentStatus.substrateTypeId ?? SubstrateType.Others,
        );

        if (this.selectShowSubstrate && task.currentStatus.substrateTypeId == null) {
            this.taskStatusForm.controls["substrateTypeOverride"].enable();
            this.refreshRequiredValidator("substrateTypeOverride", () => true);
        }
    }

    public generateName() {
        const formatString = "yyyyMMDD_HHmmss";
        this.generatedName = moment(new Date()).format(formatString);
        this.taskForm.controls["name"].setValue(this.generatedName);
    }

    public getUnit(unit: Unit) {
        if (!unit) return "";
        const foundUnit = this.allUnits.find((x) => x.value === unit);
        return foundUnit ? foundUnit.label : "";
    }

    //#endregion [Reactive form setup]

    //#region [Task rule controls setup]
    private resetTaskRule() {
        this.currentTaskRule = undefined;
        this.taskRuleIsSet = false;
        this.selectMaterial = false;
        this.selectLevel2Category = false;
        this.selectLevel3Category = false;
        this.selectLevel4Category = false;
        this.selectLevel5Category = false;
        this.selectCanBeRefreshed = false;
        this.selectIsMachineWork = false;
        this.selectCanHavePrimer = false;
        this.selectShowSubstrate = false;
        this.selectShowCategories = false;
        this.selectColor = false;
        this.signMaterials = [];
        this.removeTypes = [];
        this.signCategoryValues = [];
    }

    private filterTaskStatuses() {
        if (this.task && this.taskStatuses && !this.task.id) {
            // when creating new task -> scheduled and OnHold tasks can't be selected
            this.filteredTaskStatuses = this.taskStatuses.filter(
                (x) => x.value === TaskStatus.InProgress || x.value === TaskStatus.Finished,
            );
        } else {
            this.filteredTaskStatuses = this.taskStatuses;
        }
    }

    private applyTaskRule(task: ITask) {
        if (!task.currentStatus.taskTypeId || !this.taskTypes || this.taskTypes.length === 0) {
            this.resetTaskRule();
            return;
        }
        const rule = this.taskRules
            ? this.taskRules.find((r) => r.taskTypeId === task.currentStatus.taskTypeId)
            : undefined;
        if (!rule) {
            this.resetTaskRule();
            return;
        }

        this.refreshTaskRule(rule);

        const currentMaterial = this.getMaterialInfo(task.currentStatus.signMaterialId);
        this.setSelectIsMachineWork(currentMaterial, rule);
        this.setSelectCanHavePrimer(currentMaterial);
        this.setSelectColor(currentMaterial);
        this.setCategoryValues(currentMaterial);
        this.handleCategoryChange(task.currentStatus.signCategoryId, true);
        this.handleCategoryLevel2Change(task.currentStatus.signCategoryLevel2Id, true);
        this.handleCategoryLevel3Change(task.currentStatus.signCategoryLevel3Id, true);
        this.handleCategoryLevel4Change(task.currentStatus.signCategoryLevel4Id, true);
        this.handleCategoryLevel5Change(task.currentStatus.signCategoryLevel5Id, true);

        this.patchTaskRuleFormValues(this.task);
        // Prefer the current category configuration so existing tasks pick up newer field
        // options (e.g. multiple stop lines) instead of being stuck on the configuration
        // snapshot stored when the task was created. The configuration only controls field
        // visibility/options, not the saved values. Fall back to the stored snapshot.
        this.propertyConfiguration =
            this.currentSignCategory?.taskPropertyConfiguration ??
            this.task.currentStatus.propertyConfiguration ??
            new TaskPropertyConfiguration();
        this.patchPropertyLists(this.propertyConfiguration);
        this.patchCustomValuesOnInit(task);
        this.taskRuleIsSet = true;
    }

    // Set custom values on init if visible and there's no standard list
    private patchCustomValuesOnInit(task: ITask) {
        if (this.displayCustomDiameter && this.hideDiameterList) {
            this.taskStatusForm.controls["customDiameter"].patchValue(task.currentStatus.diameter);
        }
        if (task.currentStatus.block1Width) {
            this.taskStatusForm.controls["block1Width"].patchValue(task.currentStatus.block1Width);
        }
        if (task.currentStatus.block2Width) {
            this.taskStatusForm.controls["block2Width"].patchValue(task.currentStatus.block2Width);
        }
        if (task.currentStatus.block1Length) {
            this.taskStatusForm.controls["block1Length"].patchValue(task.currentStatus.block1Length);
        }
        if (task.currentStatus.block2Length) {
            this.taskStatusForm.controls["block2Length"].patchValue(task.currentStatus.block2Length);
        }
        if (this.displayCustomHeight && this.hideHeightList) {
            this.taskStatusForm.controls["customHeight"].patchValue(task.currentStatus.height);
        }
        if (this.displayCustomColoredLength && this.hideColoredLengthList) {
            this.taskStatusForm.controls["customColoredLength"].patchValue(task.currentStatus.coloredLength);
        }
        if (this.displayCustomColoredWidth && this.hideColoredWidthList) {
            this.taskStatusForm.controls["customColoredWidth"].patchValue(task.currentStatus.coloredWidth);
        }
        if (this.displayCustomLength && this.hideLengthList) {
            this.taskStatusForm.controls["customLength"].patchValue(task.currentStatus.length);
        }
        if (this.displayCustomWidth && this.hideWidthList) {
            this.taskStatusForm.controls["customWidth"].patchValue(task.currentStatus.width);
        }
        if (this.displayCustomLine15WidthWhite && this.hideWidth15WhiteList) {
            this.taskStatusForm.controls["customLine15WidthWhite"].patchValue(task.currentStatus.line15WidthWhite);
        }
        if (this.displayCustomLine15WidthGreen && this.hideWidth15GreenList) {
            this.taskStatusForm.controls["customLine15WidthGreen"].patchValue(task.currentStatus.line15WidthGreen);
        }
        if (this.displayCustomLine30WidthGreen && this.hideWidth30GreenList) {
            this.taskStatusForm.controls["customLine30WidthGreen"].patchValue(task.currentStatus.line30WidthGreen);
        }
        if (this.displayCustomSpeed && this.hideSpeedList) {
            this.taskStatusForm.controls["customSpeed"].patchValue(task.currentStatus.speed);
        }
        if (this.displayCustomText && this.hideTextList) {
            this.taskStatusForm.controls["customText"].patchValue(task.currentStatus.text);
        }
    }

    private refreshTaskRule(rule: ITaskRule) {
        this.currentTaskRule = rule;
        this.setSelectMaterial(rule);
        this.setMaterialValues(rule);
        this.setRemoveValues(rule);
        this.setSelectCanBeRefreshed(rule);
        this.setSelectShowSubstrate(rule);
        this.setSelectShowCategories(rule);
        this.resetCategoryLevels();
        this.setSelectColor(undefined);
        this.setSelectIsMachineWork(undefined);
        this.setSelectCanHavePrimer(undefined);
    }

    private setSelectIsMachineWork(material: ISignMaterialInfo = undefined, rule: ITaskRule = undefined) {
        if (this.taskStatusForm.get("taskTypeId").value === TaskType.RemoveMarkings) {
            this.selectIsMachineWork = false;
            return;
        }
        if (material && material.canBeMachineWork) this.selectIsMachineWork = true;
        else if (rule && rule.canBeMachineWork) this.selectIsMachineWork = true;
        else this.selectIsMachineWork = false;
    }

    private setSelectCanHavePrimer(material: ISignMaterialInfo) {
        this.selectCanHavePrimer = material && material.canHavePrimer;
    }

    private setSelectShowCategories(rule: ITaskRule) {
        this.selectShowCategories = rule && rule.showCategories;
    }

    private setSelectShowSubstrate(rule: ITaskRule) {
        this.selectShowSubstrate = rule && rule.showSubstrates;
        this.refreshRequiredValidator(
            "substrateTypeId",
            () => this.selectShowSubstrate && this.taskStatusForm.controls["substrateTypeId"].value != "",
        );
    }

    private setSelectColor(material: ISignMaterialInfo) {
        this.selectColor = !!material && material.requiresColor;
        this.refreshRequiredValidator(
            "customSignColor",
            () => this.selectColor && this.propertyConfiguration && !this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor,
        );
        this.refreshRequiredValidator(
            "signColorId",
            () => this.selectColor && this.propertyConfiguration && this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor,
        );
        this.setDefaultColor();
    }

    private setDefaultColor() {
        if (
            this.selectColor &&
            this.propertyConfiguration &&
            !this.propertyConfiguration.showSignColorList &&
            !this.propertyConfiguration.hideColor &&
            !this.taskStatusForm.controls["customSignColor"].value
        ) {
            let defaultColor: string;
            if (this.propertyConfiguration.defaultCustomSignColor !== undefined)
                defaultColor = this.propertyConfiguration.defaultCustomSignColor.toString().toLowerCase();
            else
                defaultColor =
                    this.taskStatusForm.get("taskTypeId").value === "permanentMarking" ? "#f6f6f6" : "#f0ca00";

            this.taskStatusForm.controls["customSignColor"].patchValue(defaultColor);
        }
    }

    private resetCategoryLevels() {
        this.setLevel2Category(undefined);
        this.setLevel3Category(undefined);
        this.setLevel4Category(undefined);
        this.setLevel5Category(undefined);
    }

    // Category Levels
    private setLevel2Category(category: ISignCategory, initialCall = false) {
        this.selectLevel2Category = this.canSelectLevelCategory(category);
        this.refreshRequiredValidator("signCategoryLevel2Id", () => this.selectLevel2Category);
        this.signCategoryLevel2Values = this.getCategoryValues(this.selectLevel2Category, category);
        this.taskStatusForm.controls["signCategoryLevel2Id"].patchValue(null);
        if (category != null && !this.selectLevel2Category) {
            this.currentSignCategory = category;
        }
        if (!initialCall) {
            this.applyNewPropertyConfiguration(category, this.selectLevel2Category);
        }
    }

    private setLevel3Category(category: ISignCategory, initialCall = false) {
        this.selectLevel3Category = this.canSelectLevelCategory(category);
        this.refreshRequiredValidator("signCategoryLevel3Id", () => this.selectLevel3Category);
        this.signCategoryLevel3Values = this.getCategoryValues(this.selectLevel3Category, category);
        this.taskStatusForm.controls["signCategoryLevel3Id"].patchValue(null);
        if (category != null && !this.selectLevel3Category) {
            this.currentSignCategory = category;
        }
        if (!initialCall) {
            this.applyNewPropertyConfiguration(category, this.selectLevel3Category);
        }
    }

    private setLevel4Category(category: ISignCategory, initialCall = false) {
        this.selectLevel4Category = this.canSelectLevelCategory(category);
        this.refreshRequiredValidator("signCategoryLevel4Id", () => this.selectLevel4Category);
        this.signCategoryLevel4Values = this.getCategoryValues(this.selectLevel4Category, category);
        this.taskStatusForm.controls["signCategoryLevel4Id"].patchValue(null);
        if (category != null && !this.selectLevel4Category) {
            this.currentSignCategory = category;
        }
        if (!initialCall) {
            this.applyNewPropertyConfiguration(category, this.selectLevel4Category);
        }
    }

    private setLevel5Category(category: ISignCategory, initialCall = false) {
        this.selectLevel5Category = this.canSelectLevelCategory(category);
        this.refreshRequiredValidator("signCategoryLevel5Id", () => this.selectLevel5Category);
        this.signCategoryLevel5Values = this.getCategoryValues(this.selectLevel5Category, category);
        this.taskStatusForm.controls["signCategoryLevel5Id"].patchValue(null);
        if (category != null && !this.selectLevel5Category) {
            this.currentSignCategory = category;
        }
        if (!initialCall) {
            this.applyNewPropertyConfiguration(category, this.selectLevel5Category);
        }
    }

    /**
     * Apply configuration based on selected category structure
     * Only if it's a valid category AND there are no sublevels
     */
    private applyNewPropertyConfiguration(category: ISignCategory, hasNextLevel: boolean) {
        if (category != null && !hasNextLevel) {
            this.resetTaskProperties();
            this.propertyConfiguration =
                category.taskPropertyConfiguration == null
                    ? new TaskPropertyConfiguration()
                    : category.taskPropertyConfiguration;
            this.resetValidatorsPropertyConfiguration();
            this.patchPropertyLists(this.propertyConfiguration);
        }
    }

    private canSelectLevelCategory(category: ISignCategory) {
        return !!category && this.allSignCategories.findIndex((cs) => cs.parentId === category.signCategoryId) > -1;
    }

    private getCategoryValues(selectLevelCategory: boolean, category: ISignCategory) {
        if (selectLevelCategory) {
            let filteredSignCategories = [];
            if (this.taskStatusForm.controls["isMachineWork"].value === true) {
                filteredSignCategories = this.allSignCategories.filter(
                    (cs) => cs.parentId === category.signCategoryId && cs.canBeMachineWork,
                );
            } else {
                filteredSignCategories = this.allSignCategories.filter(
                    (cs) => cs.parentId === category.signCategoryId && cs.canBeHandWork,
                );
            }
            filteredSignCategories = filteredSignCategories.sortBy((x) => x.displayOrder);
            const filteredValues = this.allSignCategoryValues.filter(
                (m) => filteredSignCategories.findIndex((c) => c.signCategoryId === m.value) > -1,
            );
            return filteredValues.sort(function (a, b) {
                return (
                    filteredSignCategories.findIndex((item) => item.signCategoryId === a.value) -
                    filteredSignCategories.findIndex((item) => item.signCategoryId === b.value)
                );
            });
        }
    }

    private setSelectCanBeRefreshed(rule: ITaskRule) {
        this.selectCanBeRefreshed = rule && rule.canBeRefreshed;
    }

    private setSelectMaterial(rule: ITaskRule) {
        this.selectMaterial = !!rule && !!rule.signMaterialInfo && rule.signMaterialInfo.length > 0;
    }

    private setMaterialValues(rule: ITaskRule) {
        this.signMaterials =
            rule && rule.signMaterialInfo
                ? rule.signMaterialInfo.map((smi) => {
                      return this.allSignMaterials.find((m) => m.value === smi.signMaterialId);
                  })
                : [];
    }

    private setRemoveValues(rule: ITaskRule) {
        this.removeTypes =
            rule && rule.removeTypeInfo
                ? rule.removeTypeInfo.map((rmi) => {
                      return this.allRemoveTypes.find((m) => m.value === rmi.removeTypeId);
                  })
                : [];
    }

    private getMaterialInfo(selectedMaterial: SignMaterial) {
        if (!this.currentTaskRule || !this.currentTaskRule.signMaterialInfo) {
            return;
        }
        return this.currentTaskRule.signMaterialInfo.find((m) => m.signMaterialId === selectedMaterial);
    }

    private setCategoryValues(material: ISignMaterialInfo) {
        let categories: ISignCategory[] = [];
        if (!material) {
            if (this.currentTaskRule.isMarking) {
                categories = this.allSignCategories.filter(
                    (sc) => !sc.parentId && sc.isMarking === this.currentTaskRule.isMarking,
                );
            } else {
                categories = this.allSignCategories.filter(
                    (sc) =>
                        !sc.parentId &&
                        sc.isMarking === this.currentTaskRule.isMarking &&
                        sc.taskTypeId === this.currentTaskRule.taskTypeId,
                );
            }
        } else {
            const isMachineWork =
                material.canBeMachineWork && this.taskStatusForm.controls["isMachineWork"].value === true;
            const taskTypeId = this.taskStatusForm.controls["taskTypeId"].value;
            if (taskTypeId === TaskType.RemoveMarkings) {
                const removeTypeId = this.taskStatusForm.controls["removeTypeId"].value;
                const taskTypeRule = this.taskRules.find((r) => r.taskTypeId === taskTypeId);
                const removeTypeRule = taskTypeRule?.removeTypeInfo.find((rt) => rt.removeTypeId === removeTypeId);
                const rule = removeTypeRule?.signMaterialInfo.find((m) => m.signMaterialId === material.signMaterialId);
                if (rule) {
                    const allowedCategories = rule.signCategoryInfo.map((sci) => sci.signCategoryId);
                    categories = this.allSignCategories.filter((sc) => allowedCategories.includes(sc.signCategoryId));
                }
            }
            if (categories.length === 0) {
                if (isMachineWork) {
                    if (this.currentTaskRule.isMarking) {
                        categories = this.allSignCategories.filter(
                            (sc) =>
                                !sc.parentId && sc.canBeMachineWork && sc.isMarking === this.currentTaskRule.isMarking,
                        );
                    } else {
                        categories = this.allSignCategories.filter(
                            (sc) =>
                                !sc.parentId &&
                                sc.canBeMachineWork &&
                                sc.isMarking === this.currentTaskRule.isMarking &&
                                sc.taskTypeId === this.currentTaskRule.taskTypeId,
                        );
                    }
                } else {
                    if (this.currentTaskRule.isMarking) {
                        categories = this.allSignCategories.filter(
                            (sc) => !sc.parentId && sc.canBeHandWork && sc.isMarking === this.currentTaskRule.isMarking,
                        );
                    } else {
                        categories = this.allSignCategories.filter(
                            (sc) =>
                                !sc.parentId &&
                                sc.canBeHandWork &&
                                sc.isMarking === this.currentTaskRule.isMarking &&
                                sc.taskTypeId === this.currentTaskRule.taskTypeId,
                        );
                    }
                }
            }
        }

        if (categories) {
            categories = categories.sortBy((x) => x.displayOrder);
            const filteredValues = this.allSignCategoryValues.filter(
                (m) => categories.findIndex((c) => c.signCategoryId === m.value) > -1,
            );
            this.signCategoryValues = filteredValues.sort(function (a, b) {
                return (
                    categories.findIndex((item) => item.signCategoryId === a.value) -
                    categories.findIndex((item) => item.signCategoryId === b.value)
                );
            });
        } else {
            this.signCategoryValues = [];
        }
    }

    private resetTaskProperties() {
        this.resetFormControlValues(
            "note",
            "customSignColor",
            "signColorId",
            "markingsCount",
            "hoursCount",
            "minutesCount",
            "length",
            "customLength",
            "width",
            "customWidth",
            "coloredLength",
            "coloredWidth",
            "customColoredWidth",
            "dropdownDimension",
            "customDimensionLength",
            "customDimensionWidth",
            "customDimensionDescription",
            "type",
            "bannerCount",
            "logoCount",
            "diameter",
            "customDiameter",
            "customLElementWidth",
            "customLElementLength",
            "dropdownLElementDimension",
            "tElementCount",
            "lElementCount",
            "tramElementCount",
            "height",
            "customHeight",
            "text",
            "customText",
            "speed",
            "customSpeed",
            "placenumber",
            "block1Amount",
            "block1Length",
            "block1Width",
            "block2Amount",
            "block2Width",
            "block1Length",
            "lengthA",
            "lengthB",
            "lengthC",
            "lengthD",
            "whiteSubstrateLength",
            "whiteSubstrateWidth",
            "whiteBandCount",
            "redBandCount",
            "yellowBandCount",
            "orangeBandCount",
            "greenBandCount",
            "blueBandCount",
            "purpleBandCount",
            "bendCount",
            "arrowHeadCount",
            "roundedEndCount",
            "shortPieceCount",
        );
        this.displayCustomLength = false;
        this.hideLengthList = false;
        this.displayCustomWidth = false;
        this.hideWidthList = false;
        this.displayCustomColoredWidth = false;
        this.hideColoredWidthList = false;
        this.displayCustomColoredLength = false;
        this.hideColoredLengthList = true;
        this.displayCustomDimension = false;
        this.hideDimensionList = false;
        this.displayCustomDiameter = false;
        this.hideDiameterList = false;
        this.displayCustomLDimension = false;
        this.hideLElementDimensionList = false;
        this.displayCustomTDimension = false;
        this.hideTElementDimensionList = false;
        this.displayCustomHeight = false;
        this.hideHeightList = false;
        this.displayCustomText = false;
        this.hideTextList = false;
        this.displayCustomSpeed = false;
        this.displayCustomSignColor = false;
        this.hideSpeedList = false;
    }

    private resetValidatorsPropertyConfiguration() {
        this.refreshRequiredValidator(
            "customSignColor",
            () => this.selectColor && this.propertyConfiguration && !this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor,
        );
        this.refreshRequiredValidator(
            "signColorId",
            () => this.selectColor && this.propertyConfiguration && this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor,
        );
    }

    initializeWorkers(): void {
        const searchParameters = new SearchParameters();
        searchParameters.sort = [];
        searchParameters.sort.push(new SortDescriptor(SortDirection.ascending, "FirstName"));

        this.workerApi
            .getAll$(searchParameters)
            .pipe(
                map((workers: IWorker[], _index: number) => {
                    return workers.map((worker) => {
                        return {
                            label: `${worker.firstName} ${worker.lastName}`,
                            value: worker.id,
                        } as DropdownOption<number>;
                    });
                }),
            )
            .subscribe({
                next: (workers) => {
                    this.workers = workers.map((worker) => {
                        return { label: worker.label, value: worker.value } as DropdownOption<number>;
                    });
                },
                error: (_error) => {
                    this.workers = [];
                },
            });
    }

    //#endregion [Task rule controls setup]

    //#region [Server calls]
    async save() {
        this.submitting = true;
        const isValid = await this.formValidationService.checkValidity(this.taskForm);

        if (!isValid) {
            this.submitting = false;
            return;
        }
        if (this.isCreatingNewTask()) {
            this.createNew().then(() => {
                this.submitting = false;
            });
        } else {
            this.update();
        }
    }

    async saveAndCopy() {
        this.submitting = true;
        const isValid = await this.formValidationService.checkValidity(this.taskForm);

        if (!isValid) {
            this.submitting = false;
            return;
        }
        if (this.isCreatingNewTask()) {
            this.createNew(true).then(() => {
                this.submitting = false;
            });
        } else {
            this.update(true);
        }
    }

    isCreatingNewTask(): boolean {
        const taskId = this.route.snapshot.params["taskId"];
        return taskId && taskId === "new";
    }

    private async createNew(makeCopy = false, stayOnPage = false) {
        const taskCreator = Object.assign(new TaskCreator(), this.taskForm.getRawValue()) as TaskCreator;
        taskCreator.currentStatus.isVerified = false;
        taskCreator.currentStatus.taskStatusId = (this.taskForm.get("isFinished").value as boolean)
            ? TaskStatus.Finished
            : TaskStatus.InProgress;
        taskCreator.start = new Date();
        taskCreator.end = taskCreator?.currentStatus.taskStatusId === TaskStatus.Finished ? new Date() : null;
        // A coordinator creates this task for the team, not as one of its members, so it must not be stamped with his
        // worker id: that would hide it from the team's task list.
        taskCreator.currentStatus.isCoordinatorTask = this.appService.isCoordinatorControlTask;

        if (
            this.taskStatusForm.get("workerIds")?.value &&
            (<number[]>this.taskStatusForm.get("workerIds").value)?.length > 0
        ) {
            taskCreator.currentStatus.workerTasks = (<number[]>this.taskStatusForm.get("workerIds").value).map(
                (workerId: number) => {
                    return {
                        workerId: workerId,
                    } as WorkerTaskCreator;
                },
            );
        } else {
            taskCreator.currentStatus.workerTasks = null;
        }
        this.setCustomValuesOnTask(taskCreator);

        this.loaderService.show();
        const createdTask = await firstValueFrom(this.taskApi.create$(taskCreator)).finally(() =>
            this.loaderService.hide(),
        );
        this.toastService.showSuccess(this.translateService.instant("taskPage.taskCreated"));

        if (makeCopy) {
            taskCreator.name = moment().format("YYYYMMDD_HHmmss");
            taskCreator.currentStatus.taskStatusId = TaskStatus.InProgress;
            taskCreator.currentStatus.isVerified = false;
            taskCreator.end = null;
            this.loaderService.show();
            const copyTask = await firstValueFrom(this.taskApi.create$(taskCreator)).finally(() =>
                this.loaderService.hide(),
            );
            this.toastService.showSuccess(this.translateService.instant("taskPage.taskCreated"));
            await this.router.navigate([
                "/assignment",
                this.assignmentService.selectedPlannedAssignment.id,
                "task",
                copyTask.id,
            ]);
        } else {
            if (stayOnPage) {
                await this.router.navigate([
                    "/assignment",
                    this.assignmentService.selectedPlannedAssignment.id,
                    "task",
                    createdTask.id,
                ]);
            } else {
                await this.router.navigate([
                    "/assignment",
                    this.assignmentService.selectedPlannedAssignment.id,
                    "tasks",
                ]);
            }
        }
    }

    private update(makeCopy = false) {
        const onSuccess = async (savedTask: ITask) => {
            await this.onSaveSuccess(savedTask);
            if (makeCopy) {
                const taskCreator = Object.assign(new TaskCreator(), savedTask) as TaskCreator;
                taskCreator.assignmentId = savedTask.assignment.id;
                taskCreator.name = moment().format("YYYYMMDD_HHmmss");
                taskCreator.currentStatus.taskStatusId = TaskStatus.InProgress;
                taskCreator.currentStatus.isVerified = false;
                taskCreator.start = new Date();
                taskCreator.end = null;
                this.taskApi.create$(taskCreator).subscribe(onSuccessCopy, this.onSaveError.bind(this));
            }
            this.toastService.showSuccess(this.translateService.instant("taskPage.taskUpdated"));
            await this.router.navigate(["/assignment", this.assignmentService.selectedPlannedAssignment.id, "tasks"]);
        };
        const onSuccessCopy = async (newTask: ITask) => {
            this.toastService.showSuccess(this.translateService.instant("taskPage.taskCopied"));
            this.router.navigate([
                "/assignment",
                this.assignmentService.selectedPlannedAssignment.id,
                "task",
                newTask.id,
            ]);
        };

        const updater = Object.assign(new TaskCreator(), this.taskForm.getRawValue()) as TaskUpdater;

        if (
            this.task.currentStatus.taskStatusId === TaskStatus.Scheduled ||
            (updater.currentStatus.taskStatusId === TaskStatus.Finished &&
                // @ts-expect-error
                this.task.currentStatus.taskStatusId === TaskStatus.Scheduled)
        ) {
            updater.start = new Date();
            updater.currentStatus.taskStatusId =
                updater.currentStatus.taskStatusId == TaskStatus.Scheduled
                    ? TaskStatus.InProgress
                    : updater.currentStatus.taskStatusId;
            updater.name = moment().format("YYYYMMDD_HHmmss");
        }

        const updatedTask = Object.assign(new TaskUpdater(), this.task, updater) as TaskUpdater;

        const isFinished = this.taskForm.get("isFinished").value as boolean;
        if (isFinished) {
            updatedTask.currentStatus.taskStatusId = TaskStatus.Finished;
        } else {
            updatedTask.currentStatus.taskStatusId = TaskStatus.InProgress;
        }

        const wasFinished = this.task.currentStatus.taskStatusId === TaskStatus.Finished;
        updatedTask.end =
            wasFinished != this.taskForm.get("isFinished").value
                ? this.taskForm.get("isFinished").value
                    ? new Date()
                    : null
                : this.task.end;

        updatedTask.locationId = this.task.location.id;

        updatedTask.currentStatus.availableFrom = null;
        if (
            this.taskStatusForm.get("workerIds")?.value &&
            (<number[]>this.taskStatusForm.get("workerIds").value)?.length > 0
        ) {
            updatedTask.currentStatus.workerTasks = (<number[]>this.taskStatusForm.get("workerIds").value).map(
                (workerId: number) => {
                    return {
                        workerId: workerId,
                    } as WorkerTaskCreator;
                },
            );
        } else {
            updatedTask.currentStatus.workerTasks = null;
        }

        this.setCustomValuesOnTask(updatedTask);

        this.taskApi.update$(updatedTask).subscribe({
            next: onSuccess,
            error: this.onSaveError.bind(this),
            complete: () => (this.submitting = false),
        });
    }

    public setCustomValuesOnTask(creator: TaskCreator) {
        const counts = [
            "markingsCount",
            "bannerCount",
            "logoCount",
            "tElementCount",
            "lElementCount",
            "tRAMElementCount",
            "block1Amount",
            "block2Amount",
            "whiteBandCount",
            "redBandCount",
            "yellowBandCount",
            "orangeBandCount",
            "greenBandCount",
            "blueBandCount",
            "purpleBandCount",
            "bendCount",
            "arrowHeadCount",
            "roundedEndCount",
            "shortPieceCount",
        ];
        counts.forEach((count) => {
            if (creator[count] != null) {
                let newCount = creator[count] as string;
                newCount = newCount.replace(",", "");
                newCount = newCount.replace(".", "");
                creator[count] = parseInt(newCount);
            }
        });

        creator.currentStatus.substrateTypeId =
            creator.currentStatus.substrateTypeId === SubstrateType.Others
                ? null
                : creator.currentStatus.substrateTypeId;

        creator.currentStatus.isNew = this.setSelectCanBeRefreshed ? creator.currentStatus.isNew : true;
        creator.currentStatus.propertyConfiguration = this.propertyConfiguration;
        creator.currentStatus.customSignColor =
            this.selectColor && !this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor
                ? creator.currentStatus.customSignColor
                : undefined;
        creator.currentStatus.signColorId =
            this.selectColor && this.propertyConfiguration.showSignColorList && !this.propertyConfiguration.hideColor
                ? creator.currentStatus.signColorId
                : undefined;
        creator.currentStatus.width = this.propertyConfiguration.showWidth
            ? this.displayCustomWidth
                ? this.taskStatusForm.get("customWidth").value
                : creator.currentStatus.width
            : undefined;
        creator.currentStatus.widthUnitId = this.propertyConfiguration.showWidth
            ? this.propertyConfiguration.widthUnit
            : undefined;
        creator.currentStatus.length = this.propertyConfiguration.showLength
            ? this.displayCustomLength
                ? this.taskStatusForm.get("customLength").value
                : creator.currentStatus.length
            : undefined;
        creator.currentStatus.lengthUnitId = this.propertyConfiguration.showLength
            ? this.propertyConfiguration.lengthUnit
            : undefined;
        creator.currentStatus.height = this.propertyConfiguration.showHeight
            ? this.displayCustomHeight
                ? this.taskStatusForm.get("customHeight").value
                : creator.currentStatus.height
            : undefined;
        creator.currentStatus.heightUnitId = this.propertyConfiguration.showHeight
            ? this.propertyConfiguration.heightUnit
            : undefined;

        creator.currentStatus.lengthTriangle = this.propertyConfiguration.showLengthTriangle
            ? this.taskStatusForm.get("lengthTriangle").value
            : undefined;
        creator.currentStatus.lengthTriangleUnitId = this.propertyConfiguration.showLengthTriangle
            ? this.propertyConfiguration.lengthTriangleUnit
            : undefined;

        creator.currentStatus.widthTriangle = this.propertyConfiguration.showWidthTriangle
            ? this.taskStatusForm.get("widthTriangle").value
            : undefined;
        creator.currentStatus.widthTriangleUnitId = this.propertyConfiguration.showWidthTriangle
            ? this.propertyConfiguration.widthTriangleUnit
            : undefined;

        creator.currentStatus.lengthCross = this.propertyConfiguration.showLengthCross
            ? this.taskStatusForm.get("lengthCross").value
            : undefined;
        creator.currentStatus.lengthCrossUnitId = this.propertyConfiguration.showLengthCross
            ? this.propertyConfiguration.lengthCrossUnit
            : undefined;

        creator.currentStatus.widthCross = this.propertyConfiguration.showWidthCross
            ? this.taskStatusForm.get("widthCross").value
            : undefined;
        creator.currentStatus.widthCrossUnitId = this.propertyConfiguration.showWidthCross
            ? this.propertyConfiguration.widthCrossUnit
            : undefined;

        creator.currentStatus.coloredWidth = this.propertyConfiguration.showColoredWidth
            ? this.displayCustomColoredWidth
                ? this.taskStatusForm.get("customColoredWidth").value
                : creator.currentStatus.coloredWidth
            : undefined;
        creator.currentStatus.coloredWidthUnitId = this.propertyConfiguration.showColoredWidth
            ? this.propertyConfiguration.coloredWidthUnit
            : undefined;
        creator.currentStatus.coloredLength = this.propertyConfiguration.showColoredLength
            ? this.displayCustomColoredLength
                ? this.taskStatusForm.get("customColoredLength").value
                : creator.currentStatus.coloredLength
            : undefined;
        creator.currentStatus.coloredLengthUnitId = this.propertyConfiguration.showColoredLength
            ? this.propertyConfiguration.coloredLengthUnit
            : undefined;
        creator.currentStatus.whiteSubstrateLengthUnitId = this.propertyConfiguration.showWhiteSubstrateLength
            ? this.propertyConfiguration.whiteSubstrateLengthUnit
            : undefined;
        creator.currentStatus.whiteSubstrateWidthUnitId = this.propertyConfiguration.showWhiteSubstrateWidth
            ? this.propertyConfiguration.whiteSubstrateWidthUnit
            : undefined;

        creator.currentStatus.diameter = this.propertyConfiguration.showDiameter
            ? this.displayCustomDiameter
                ? this.taskStatusForm.get("customDiameter").value
                : creator.currentStatus.diameter
            : undefined;
        creator.currentStatus.diameterUnitId = this.propertyConfiguration.showDiameter
            ? this.propertyConfiguration.diameterUnit
            : undefined;
        creator.currentStatus.block1Width = this.propertyConfiguration.showBlock1Width
            ? this.taskStatusForm.get("block1Width").value
            : undefined;
        creator.currentStatus.block2Width = this.propertyConfiguration.showBlock2Width
            ? this.taskStatusForm.get("block2Width").value
            : undefined;
        creator.currentStatus.block1Length = this.propertyConfiguration.showBlock1Length
            ? this.taskStatusForm.get("block1Length").value
            : undefined;
        creator.currentStatus.block2Length = this.propertyConfiguration.showBlock2Length
            ? this.taskStatusForm.get("block2Length").value
            : undefined;
        creator.currentStatus.blocksLengthUnitId =
            this.propertyConfiguration.showBlock1Amount || this.propertyConfiguration.showBlock2Amount
                ? this.propertyConfiguration.blocksLengthUnit
                : undefined;
        creator.currentStatus.blocksWidthUnitId =
            this.propertyConfiguration.showBlock1Amount || this.propertyConfiguration.showBlock2Amount
                ? this.propertyConfiguration.blocksWidthUnit
                : undefined;
        creator.currentStatus.speed = this.propertyConfiguration.showSpeed
            ? this.displayCustomSpeed
                ? this.taskStatusForm.get("customSpeed").value
                : creator.currentStatus.speed
            : undefined;
        creator.currentStatus.text = this.propertyConfiguration.showText
            ? this.displayCustomText
                ? this.taskStatusForm.get("customText").value
                : creator.currentStatus.text
            : undefined;
        creator.currentStatus.type = this.propertyConfiguration.showType ? creator.currentStatus.type : undefined;
        creator.currentStatus.dimensionLength = this.propertyConfiguration.showDimension
            ? this.displayCustomDimension
                ? this.taskStatusForm.get("customDimensionLength").value
                : this.taskStatusForm.get("dropdownDimension").value.length
            : undefined;
        creator.currentStatus.dimensionWidth = this.propertyConfiguration.showDimension
            ? this.displayCustomDimension
                ? this.taskStatusForm.get("customDimensionWidth").value
                : this.taskStatusForm.get("dropdownDimension").value.width
            : undefined;
        creator.currentStatus.dimensionDescription = this.propertyConfiguration.showDimension
            ? this.displayCustomDimension
                ? this.taskStatusForm.get("customDimensionDescription").value
                : this.taskStatusForm.get("dropdownDimension").value.description
            : undefined;
        creator.currentStatus.dimensionUnitId = this.propertyConfiguration.showDimension
            ? this.propertyConfiguration.dimensionUnit
            : undefined;
        creator.currentStatus.lElementWidth = this.propertyConfiguration.showLElementDimension
            ? this.displayCustomLDimension
                ? this.taskStatusForm.get("customLElementWidth").value
                : this.taskStatusForm.get("dropdownLElementDimension").value.width
            : undefined;
        creator.currentStatus.lElementLength = this.propertyConfiguration.showLElementDimension
            ? this.displayCustomLDimension
                ? this.taskStatusForm.get("customLElementLength").value
                : this.taskStatusForm.get("dropdownLElementDimension").value.length
            : undefined;
        creator.currentStatus.lElementUnitId = this.propertyConfiguration.showLElementDimension
            ? this.propertyConfiguration.lElementUnit
            : undefined;
        creator.currentStatus.tElementLength = this.propertyConfiguration.showTElementDimension
            ? this.displayCustomTDimension
                ? this.taskStatusForm.get("customTElementLength").value
                : this.taskStatusForm.get("dropdownTElementDimension").value.length
            : undefined;
        creator.currentStatus.tElementWidth = this.propertyConfiguration.showTElementDimension
            ? this.displayCustomTDimension
                ? this.taskStatusForm.get("customTElementWidth").value
                : this.taskStatusForm.get("dropdownTElementDimension").value.width
            : undefined;
        creator.currentStatus.tElementUnitId = this.propertyConfiguration.showTElementDimension
            ? this.propertyConfiguration.tElementUnit
            : undefined;

        creator.currentStatus.line15LengthWhite = this.propertyConfiguration.showLine15LengthWhite
            ? creator.currentStatus.line15LengthWhite
            : undefined;
        creator.currentStatus.line15LengthWhiteUnitId = this.propertyConfiguration.showLine15LengthWhite
            ? creator.currentStatus.line15LengthWhiteUnitId
            : undefined;
        creator.currentStatus.line15WidthWhite = this.propertyConfiguration.showLine15WidthWhite
            ? this.displayCustomLine15WidthWhite
                ? this.taskStatusForm.get("customLine15WidthWhite").value
                : this.taskStatusForm.get("line15WidthWhite").value
            : undefined;
        creator.currentStatus.line15WidthWhiteUnitId = this.propertyConfiguration.showLine15WidthWhite
            ? creator.currentStatus.line15WidthWhiteUnitId
            : undefined;
        creator.currentStatus.line15LengthGreen = this.propertyConfiguration.showLine15LengthGreen
            ? creator.currentStatus.line15LengthGreen
            : undefined;
        creator.currentStatus.line15LengthGreenUnitId = this.propertyConfiguration.showLine15LengthGreen
            ? creator.currentStatus.line15LengthGreenUnitId
            : undefined;
        creator.currentStatus.line15WidthGreen = this.propertyConfiguration.showLine15WidthGreen
            ? this.displayCustomLine15WidthGreen
                ? this.taskStatusForm.get("customLine15WidthGreen").value
                : this.taskStatusForm.get("line15WidthGreen").value
            : undefined;
        creator.currentStatus.line15WidthGreenUnitId = this.propertyConfiguration.showLine15WidthGreen
            ? creator.currentStatus.line15WidthGreenUnitId
            : undefined;
        creator.currentStatus.line30LengthGreen = this.propertyConfiguration.showLine30LengthGreen
            ? creator.currentStatus.line30LengthGreen
            : undefined;
        creator.currentStatus.line30LengthGreenUnitId = this.propertyConfiguration.showLine30LengthGreen
            ? creator.currentStatus.line30LengthGreenUnitId
            : undefined;
        creator.currentStatus.line30WidthGreen = this.propertyConfiguration.showLine30WidthGreen
            ? this.displayCustomLine30WidthGreen
                ? this.taskStatusForm.get("customLine30WidthGreen").value
                : this.taskStatusForm.get("line30WidthGreen").value
            : undefined;
        creator.currentStatus.line30WidthGreenUnitId = this.propertyConfiguration.showLine30WidthGreen
            ? creator.currentStatus.line30WidthGreenUnitId
            : undefined;
        creator.currentStatus.mechelenCarSharingTextAmount = this.propertyConfiguration.showMechelenCarSharingTextAmount
            ? creator.currentStatus.mechelenCarSharingTextAmount
            : undefined;
        creator.currentStatus.octopusZeroBlackCount = this.propertyConfiguration.showOctopusZeroBlackCount
            ? creator.currentStatus.octopusZeroBlackCount
            : undefined;
        creator.currentStatus.octopusThreeBlackCount = this.propertyConfiguration.showOctopusThreeBlackCount
            ? creator.currentStatus.octopusThreeBlackCount
            : undefined;
        creator.currentStatus.octopusSchoolTextYellowCount = this.propertyConfiguration.showOctopusSchoolTextYellowCount
            ? creator.currentStatus.octopusSchoolTextYellowCount
            : undefined;
        creator.currentStatus.octopusSquaresCount = this.propertyConfiguration.showOctopusSquaresCount
            ? creator.currentStatus.octopusSquaresCount
            : undefined;
        creator.currentStatus.octopusFullLineYellowCount = this.propertyConfiguration.showOctopusFullLineYellowCount
            ? creator.currentStatus.octopusFullLineYellowCount
            : undefined;
        creator.currentStatus.octopusSchoolTextBlackYellowCount = this.propertyConfiguration.showOctopusSchoolTextBlackYellowCount
            ? creator.currentStatus.octopusSchoolTextBlackYellowCount
            : undefined;
        creator.currentStatus.octopusThirtyYellowBlackCount = this.propertyConfiguration.showOctopusThirtyYellowBlackCount
            ? creator.currentStatus.octopusThirtyYellowBlackCount
            : undefined;

        creator.currentStatus.transitionBandStatusHistory = [];
        if (this.propertyConfiguration.showCustomTransition) {
            for (let i = 0; i < this.customTransitionsForm.length; i++) {
                const data = this.customTransitionsForm.at(i).value;
                const color = this.propertyConfiguration.showCustomTransitionColor
                    ? data["customTransitionColor"]
                    : undefined;

                // Use dropdown value if available and not "Other", otherwise use custom input
                const width = data["transitionWidth"] != null ? data["transitionWidth"] : data["customTransitionWidth"];

                const transition = {
                    length: data["customTransitionLength"],
                    lengthUnitId: this.propertyConfiguration.customTransitionLengthUnit,
                    width: width,
                    widthUnitId: this.propertyConfiguration.customTransitionWidthUnit,
                    markingsCount: data["customTransitionCount"],
                    color: color,
                } as ITransitionBandStatusHistory;
                creator.currentStatus.transitionBandStatusHistory.push(transition);
            }
        }

        creator.currentStatus.stopLineBandStatusHistory = [];
        if (this.propertyConfiguration.showCustomStopLine) {
            for (let i = 0; i < this.customStopLinesForm.length; i++) {
                const data = this.customStopLinesForm.at(i).value;

                const width = data["stopLineWidth"] != null ? data["stopLineWidth"] : data["customStopLineWidth"];

                const stopLine = {
                    length: data["customStopLineLength"],
                    lengthUnitId: this.propertyConfiguration.customStopLineLengthUnit,
                    width: width,
                    widthUnitId: this.propertyConfiguration.customStopLineWidthUnit,
                    markingsCount: data["customStopLineCount"],
                } as IStopLineBandStatusHistory;
                creator.currentStatus.stopLineBandStatusHistory.push(stopLine);
            }
        }
    }

    async onSaveSuccess(savedTask: ITask) {
        this.task = await this.taskService.fetchTask(savedTask.id);
        this.taskForm.markAsPristine();
        this.submitting = false;
    }

    onSaveError() {
        this.submitting = false;
    }

    private async initDropdowns() {
        const [
            taskRules,
            taskTypes,
            signMaterials,
            removeTypes,
            signCategoriesResult,
            signCategoryValues,
            substrateTypes,
            markingPropertyTypeValues,
            signColorOptions,
            units,
            taskStatuses,
        ] = await Promise.all([
            firstValueFrom(this.taskRulesApi.getAll$()),
            this.fillTaskTypeDropdown(this.translateService.currentLang),
            this.fillTaskSignMaterialDropdown(this.translateService.currentLang),
            this.fillTaskRemoveTypeDropdown(this.translateService.currentLang),
            firstValueFrom(this.signCategoryApi.search$()),
            this.fillTaskSignCategoryDropdown(this.translateService.currentLang),
            this.fillTaskSubstrateTypes(this.translateService.currentLang),
            this.fillMarkingPropertyTypeDropdown(this.translateService.currentLang),
            this.fillSignColorTypeDropdown(this.translateService.currentLang),
            this.fillUnitDropdown(this.translateService.currentLang),
            this.fillTaskStatusesDropdown(this.translateService.currentLang),
        ]);

        this.taskRules = taskRules;
        this.taskTypes = taskTypes;
        this.allSignMaterials = signMaterials;
        this.allRemoveTypes = removeTypes;
        this.allSignCategories = signCategoriesResult.data;
        this.allSignCategoryValues = signCategoryValues;
        this.allSubstrateTypes = substrateTypes;
        this.allMarkingPropertyTypeValues = markingPropertyTypeValues;
        this.allSignColorOptions = signColorOptions;
        this.allUnits = units;
        this.taskStatuses = taskStatuses;

        this.initializeWorkers();
    }

    //region Fill dropdowns

    private async fillUnitDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const units = this.domainDataService.domainData.enums["unit"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(units).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillSignColorTypeDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const signColorTypes = this.domainDataService.domainData.enums["signColorType"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(signColorTypes).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillMarkingPropertyTypeDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const markingPropertyTypes = this.domainDataService.domainData.enums["markingPropertyType"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(markingPropertyTypes).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskTypeDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const taskTypes = this.domainDataService.domainData.enums["taskType"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(taskTypes).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskSignMaterialDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const signMaterials = this.domainDataService.domainData.enums["signMaterial"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(signMaterials).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskRemoveTypeDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const removeTypes = this.domainDataService.domainData.enums["removeType"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(removeTypes).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskStatusesDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const taskStatuses = this.domainDataService.domainData.enums["taskStatus"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(taskStatuses).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskSignCategoryDropdown(language = "en"): Promise<DropdownOption<string>[]> {
        const signCategories = this.domainDataService.domainData.enums["signCategoryValue"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(signCategories).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        return dropDownValues;
    }

    private async fillTaskSubstrateTypes(language = "en"): Promise<DropdownOption<string>[]> {
        const substrateTypes = this.domainDataService.domainData.enums["substrateType"].values;

        const dropDownValues: DropdownOption<string>[] = [];
        Object.entries(substrateTypes).forEach(([key, value]) => {
            dropDownValues.push({
                label: value.translations[language],
                value: key,
            });
        });
        dropDownValues.push({
            label: this.translateService.instant("taskPage.other"),
            value: SubstrateType.Others,
        });

        return dropDownValues;
    }

    //endregion

    //#endregion [Server calls]

    //#region [Helpers]
    private resetFormControlValues(...formControlNames: string[]) {
        formControlNames.forEach((controlName) => {
            if (!this.taskForm.controls[controlName]) {
                if (!this.taskStatusForm.controls[controlName]) {
                    return;
                }

                this.taskStatusForm.controls[controlName].setValue(null);
                return;
            }
            this.taskForm.controls[controlName].setValue(null);
        });
    }

    private refreshRequiredValidator(controlName: string, condition: () => boolean) {
        if (condition()) {
            if (this.taskForm.controls[controlName]) {
                this.taskForm.controls[controlName].setValidators([Validators.required]);
            } else {
                this.taskStatusForm.controls[controlName].setValidators([Validators.required]);
            }
        } else {
            if (this.taskForm.controls[controlName]) {
                this.taskForm.controls[controlName].clearValidators();
            } else {
                this.taskStatusForm.controls[controlName].clearValidators();
            }
        }
        if (this.taskForm.controls[controlName]) {
            this.taskForm.controls[controlName].updateValueAndValidity();
        } else {
            this.taskStatusForm.controls[controlName].updateValueAndValidity();
        }
    }

    //#endregion [Helpers]

    get location(): ILocation {
        return this.taskForm.get("location").value;
    }

    getAddress(address: IAddress) {
        if (this.location?.address) {
            return this.locationService.convertToAddressString(address);
        }
        return "";
    }

    @ViewChild(MapsDialogComponent) mapsDialog!: MapsDialogComponent;

    openMapsDialog(event: Event) {
        this.mapsDialog.address = this.getAddress(this.location.address);
        this.mapsDialog.location = this.location;
        this.mapsDialog.open(event);
    }

    async onSaveModal(location: ILocation) {
        this.taskForm.get("location").patchValue(location);

        if (!this.isCreatingNewTask()) {
            const locationUpdater = new LocationUpdater({
                ...this.task.location,
                coordinate: location.coordinate,
                address: location.address,
            });
            await firstValueFrom(this.locationApi.update$(locationUpdater));
        }
    }

    //region Attachment
    get attachments(): FormArray<FormGroup> {
        return this.taskForm.get("attachmentsForm") as unknown as FormArray<FormGroup>;
    }

    async onAttachmentSubmit(image: string, coordinates?: ICoordinate) {
        if (!this.task?.name) {
            this.task = await this.taskService.fetchTask(this.route.snapshot.params["taskId"]);
        }

        if (this.task.currentStatus.taskStatusId === TaskStatus.Scheduled) {
            const updatedTask = Object.assign(new TaskUpdater(), this.task) as TaskUpdater;
            updatedTask.assignmentId = this.task.assignment.id;
            updatedTask.currentStatus.availableFrom = null;
            updatedTask.currentStatus.taskStatusId = TaskStatus.InProgress;
            updatedTask.currentStatus.workerTasks = null;
            updatedTask.id = this.task.id;
            updatedTask.name = moment().format("YYYYMMDD_HHmmss");
            updatedTask.start = new Date();
            const task = await firstValueFrom(this.taskApi.update$(updatedTask));
            this.task = await this.taskService.fetchTask(task.id);
            this.setTitle();
        }

        if (!this.isCreatingNewTask()) {
            if (!coordinates) {
                coordinates = await this.locationService.getCurrentCoordinates().catch(() => {
                    this.toastService.showWarning("location.usedDefault");
                    return this.assignmentService.selectedPlannedAssignment.assignment.location.coordinate;
                });
            }
            const addressWithTimeZone = await this.locationService.getAddressFromCoordinates(coordinates);

            const now = moment().format("YYYYMMDD_HHmmss");
            const imageName = `${this.task.name}_${now}.webp`;

            const location = {
                coordinate: coordinates,
                address: addressWithTimeZone.address,
                code: `${this.task.name}_${now}`,
            } as ILocation;

            const attachment = await this.taskService.uploadTaskAttachment(imageName, this.task.id, image, location);
            this.task = await this.taskService.fetchTask(this.task.id);

            const formGroup = this.formBuilder.group({
                attachmentId: attachment.id,
                attachmentImage: image,
                attachmentDescription: "",
            });

            this.attachments.push(formGroup);
        }
    }

    async onAttachmentDelete(attachmentId: number) {
        if (!this.isCreatingNewTask()) {
            await this.taskService.deleteTaskAttachment(attachmentId);

            this.task = await this.taskService.fetchTask(this.task.id);
            this.attachments.removeAt(
                this.attachments.controls.findIndex((control) => control.get("attachmentId").value === attachmentId),
            );
        }
    }

    async onAttachmentDescriptionChange(event: Event, photoForm: FormGroup) {
        const attachmentId = photoForm.controls["attachmentId"].value;
        const attachmentDescription = photoForm.controls["attachmentDescription"].value;

        const attachment = this.task.attachments.find((a) => a.id === attachmentId);
        attachment.description = attachmentDescription;

        await this.taskService.updateTaskAttachment(attachment, this.task.id);
    }

    //endregion

    async addPhoto(mPhotoInput: PhotoInputComponent) {
        if (!this.isCreatingNewTask()) {
            mPhotoInput.open();
            return;
        }
        const isValid = await this.formValidationService.checkValidity(this.taskForm);

        if (!isValid) {
            this.toastService.showWarning(this.translateService.instant("taskPage.invalidForm"));
            return;
        }
        await this.createNew(false, true);
        mPhotoInput.open();
    }

    async onFileLoadEnd(event: PhotoInputFileLoadEnd) {
        let coordinates: ICoordinate;
        if (event.metadata && event.metadata?.longitude && event.metadata?.latitude) {
            coordinates = {
                latitude: event.metadata.latitude,
                longitude: event.metadata.longitude,
            };
        }
        await this.onAttachmentSubmit(event.base64image, coordinates);
    }

    protected readonly SubstrateType = SubstrateType;
}
